/**
 * HealthBars — the plate over a fighter's head during a battle: name, role +
 * weapon, an hp bar and a morale bar, tinted by side (attackers red-gold,
 * defenders ice-blue). Canvas → sprite, redrawn only when the inputs change.
 */
import * as THREE from 'three/webgpu';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { disposeLater } from '../../lib/gpu_dispose.js';

/**
 * `hp` and `morale` are PERCENTAGES, 0..100 — `draw` divides both by 100 and
 * the redraw cache keys on `Math.round` of them. Stated explicitly because it
 * was not: BountyHunterModule passed a 0..1 fraction, so every bounty-hunter
 * plate drew its health at ~1 % and its morale at 1 % of the bar (2026-09-14).
 */
export interface PlateState { name: string; sub: string; hp: number; morale: number; side: 'a' | 'b'; state: string }

export interface Plate {
  sprite: THREE.Sprite;
  draw(s: PlateState): void;
  dispose(): void;
}

export const SIDE_COLOR: Record<'a' | 'b', string> = { a: '#ff9a6a', b: '#7fd3ff' };

export function makePlate(): Plate {
  const cv = document.createElement('canvas');
  cv.width = 256; cv.height = 76;
  const g = cv.getContext('2d')!;
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(markMrtOverlay(new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true })));
  sprite.scale.set(2.4, 0.71, 1);
  let last = '';
  return {
    sprite,
    draw(s: PlateState): void {
      const key = `${s.name}|${s.sub}|${Math.round(s.hp)}|${Math.round(s.morale)}|${s.side}|${s.state}`;
      if (key === last) return;
      last = key;
      const accent = SIDE_COLOR[s.side];
      g.clearRect(0, 0, 256, 76);
      g.textAlign = 'center';
      g.lineWidth = 4;
      g.strokeStyle = 'rgba(0,0,0,0.85)';
      g.font = '700 19px system-ui';
      g.fillStyle = s.state === 'fallen' ? '#9a9aa6' : accent;
      g.strokeText(s.name, 128, 19);
      g.fillText(s.name, 128, 19);
      g.font = '12px system-ui';
      g.fillStyle = '#d6dff0';
      const sub = s.state === 'fallen' ? 'fallen' : s.state === 'routed' ? 'routed — running' : s.sub;
      g.strokeText(sub, 128, 35);
      g.fillText(sub, 128, 35);
      // hp
      const hp = Math.max(0, Math.min(1, s.hp / 100));
      g.fillStyle = 'rgba(10,14,22,0.85)';
      g.fillRect(40, 42, 176, 12);
      g.fillStyle = hp > 0.5 ? '#7bc86c' : hp > 0.25 ? '#e3b341' : '#e5534b';
      g.fillRect(42, 44, 172 * hp, 8);
      // morale
      const mo = Math.max(0, Math.min(1, s.morale / 100));
      g.fillStyle = 'rgba(10,14,22,0.85)';
      g.fillRect(40, 57, 176, 9);
      g.fillStyle = mo > 0.3 ? '#6fa8ff' : '#c57bff';
      g.fillRect(42, 59, 172 * mo, 5);
      g.font = '10px system-ui';
      g.fillStyle = '#b9c8dd';
      g.textAlign = 'left';
      g.fillText('hp', 22, 52); g.fillText('morale', 4, 66);
      tex.needsUpdate = true;
    },
    dispose(): void {
      sprite.parent?.remove(sprite);
      disposeLater(tex);            // deferred: the plate may be in the frame being submitted
      disposeLater(sprite.material);
    },
  };
}
